import type { Session } from "../game/session.js";
import { randomNickname, type UiState } from "../game/state.js";
import { layoutHintPouches } from "../render/widgets/cups.js";

export type BtnKind = "primary" | "ghost" | "danger" | "wood" | "text" | "silent";

export type UiButton = {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  kind: BtnKind;
  sub?: string;
};

export function hitButton(buttons: readonly UiButton[], x: number, y: number): UiButton | null {
  for (let i = buttons.length - 1; i >= 0; i--) {
    const b = buttons[i];
    if (!b) continue;
    if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return b;
  }
  return null;
}

function btn(
  id: string,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  kind: BtnKind = "wood",
  sub?: string,
): UiButton {
  const b: UiButton = { id, label, x, y, w, h, kind };
  if (sub) b.sub = sub;
  return b;
}

export type Rect = { x: number; y: number; w: number; h: number };

/** 贴在微信胶囊左侧，与胶囊垂直居中，绝不伸进胶囊区域。 */
export function leftOfCapsule(
  ui: UiState,
  w: number,
  bw: number,
  bh: number,
  extraRight = 0,
): { x: number; y: number } {
  const capLeft = ui.capsuleLeft > 80 && ui.capsuleLeft < w - 24 ? ui.capsuleLeft : w - 96;
  const x = Math.max(8, capLeft - 12 - extraRight - bw);
  const capTop = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const capH = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  const y = Math.max(ui.safeTop > 0 ? ui.safeTop : 6, capTop + (capH - bh) / 2);
  return { x, y };
}

export function settingsGearRect(ui: UiState, w: number): Rect {
  const bw = 32;
  const bh = 32;
  const av = 32;
  const pos = leftOfCapsule(ui, w, bw, bh, av + 8);
  return { x: pos.x, y: pos.y, w: bw, h: bh };
}

function settingsGear(ui: UiState, w: number): UiButton {
  const r = settingsGearRect(ui, w);
  return btn("settings", "", r.x - 4, r.y - 6, r.w + 8, r.h + 12, "silent");
}

export type OpponentHud = {
  barH: number;
  row: Rect;
  identity: Rect;
  cups: Rect;
  surrender: Rect;
  roundPill: Rect;
  clock: Rect;
  cards: Rect[];
};

/** 对局顶栏：回合胶囊 / 抢答时钟 / 弃局认输，下方等宽对手卡。 */
export function opponentHudLayout(
  w: number,
  ui: Pick<UiState, "safeTop" | "capsuleTop" | "capsuleHeight" | "mode" | "peers">,
): OpponentHud {
  const capTop = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const capH = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  const capBot = capTop + capH;
  const pad = 12;
  const pillH = 28;
  const pillY = capBot + 8;
  const surrender: Rect = { x: w - pad - 76, y: pillY, w: 76, h: pillH };
  const rush = ui.mode === "RUSH";
  const clockW = rush ? 72 : 0;
  const clock: Rect = { x: (w - Math.max(clockW, 1)) / 2, y: pillY, w: clockW, h: pillH };
  const roundRight = rush ? clock.x - 8 : surrender.x - 8;
  const roundPill: Rect = { x: pad, y: pillY, w: Math.max(96, roundRight - pad), h: pillH };
  const n = ui.mode === "RUSH" ? Math.max(1, Math.min(3, ui.peers.length || 2)) : 1;
  const gap = 8;
  const cardY = pillY + pillH + 8;
  const cardH = 52;
  const inner = w - pad * 2;
  const cardW = (inner - gap * (n - 1)) / n;
  const cards: Rect[] = [];
  for (let i = 0; i < n; i++) {
    cards.push({ x: pad + i * (cardW + gap), y: cardY, w: cardW, h: cardH });
  }
  const first = cards[0] ?? { x: pad, y: cardY, w: inner, h: cardH };
  const barH = cardY + cardH + 8;
  return {
    barH,
    row: { x: 0, y: pillY, w, h: pillH + 8 + cardH },
    identity: first,
    cups: first,
    surrender,
    roundPill,
    clock,
    cards,
  };
}

export type HallHomeLayout = {
  headerY: number;
  title: Rect;
  match: Rect;
  rush3: Rect;
  rush4: Rect;
  room: Rect;
  create: Rect;
  join: Rect;
  rank: Rect;
};

export function hallHomeLayout(w: number, h: number, ui: UiState): HallHomeLayout {
  const capTop = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const capH = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  const headerY = capTop + capH + 10;
  const x = 16;
  const inner = w - 32;
  const gap = 10;
  const compact = h < 720;
  let y = headerY + 4;
  const title: Rect = { x, y, w: inner, h: 28 };
  y += 34;
  const match: Rect = { x, y, w: inner, h: compact ? 118 : 136 };
  y += match.h + gap;
  const rushH = compact ? 96 : 108;
  const rushW = (inner - gap) / 2;
  const rush3: Rect = { x, y, w: rushW, h: rushH };
  const rush4: Rect = { x: x + rushW + gap, y, w: rushW, h: rushH };
  y += rushH + gap;
  const room: Rect = { x, y, w: inner, h: 116 };
  const bw = (inner - 32 - 10) / 2;
  const create: Rect = { x: x + 16, y: y + 58, w: bw, h: 44 };
  const join: Rect = { x: create.x + bw + 10, y: create.y, w: bw, h: 44 };
  y += room.h + gap;
  const rankBottom = h - Math.max(12, ui.safeBottom) - 8;
  const rank: Rect = { x, y, w: inner, h: Math.max(92, rankBottom - y) };
  return { headerY, title, match, rush3, rush4, room, create, join, rank };
}

/** 大厅榜条中名次行区域（标题栏以下），与 drawRankStrip 保持一致。 */
export function hallRankRows(r: Rect): Rect {
  const top = r.y + 46;
  return { x: r.x + 8, y: top, w: r.w - 16, h: Math.max(40, r.y + r.h - 10 - top) };
}

export function hallChrome(w: number, h: number, ui: UiState): {
  titleY: number;
  titleSize: number;
  sheetY: number;
  sheetH: number;
  tagY: number;
  btnStart: number;
} {
  const home = hallHomeLayout(w, h, ui);
  return {
    titleY: home.headerY,
    titleSize: 18,
    sheetY: home.match.y,
    sheetH: home.match.h,
    tagY: home.title.y + 14,
    btnStart: home.match.y,
  };
}

export function layoutLogin(w: number, h: number, ui: UiState): UiButton[] {
  const bw = Math.min(280, w - 48);
  const cx = (w - bw) / 2;
  if (ui.useWxProfile) {
    const enterY = h * 0.68;
    const policy = btn("privacy-read", "《用户隐私保护指引》", cx, enterY + 60, bw, 28, "text");
    if (!ui.privacyAgreed) {
      return [btn("privacy-open", "阅读并同意隐私政策", cx, enterY, bw, 52, "primary"), policy];
    }
    const label = ui.token && ui.roomId ? "恢复对局" : ui.wxProfileReady ? "进入茶馆" : "微信授权进入";
    return [btn("enter", label, cx, enterY, bw, 52, "primary"), policy];
  }
  const by = h * 0.62;
  return [
    btn("nick-reroll", "换一个雅号", cx, by, bw, 36, "text"),
    btn("enter", ui.token && ui.roomId ? "恢复对局" : "进入茶馆", cx, by + 44, bw, 52, "primary"),
  ];
}

export function layoutHall(w: number, h: number, ui: UiState): UiButton[] {
  if (ui.hallMenu === "rush-size") {
    const bw = Math.min(300, w - 48);
    const cx = (w - bw) / 2;
    const start = hallHomeLayout(w, h, ui).match.y;
    return [
      settingsGear(ui, w),
      btn("rush-3", "抢答 3 人", cx, start, bw, 52, "primary"),
      btn("rush-4", "抢答 4 人", cx, start + 60, bw, 48, "wood"),
      btn("rush-back", "返回茶馆", cx, start + 120, bw, 44, "text"),
    ];
  }
  const g = hallHomeLayout(w, h, ui);
  return [
    settingsGear(ui, w),
    btn("match", "", g.match.x, g.match.y, g.match.w, g.match.h, "silent"),
    btn("rush-3", "", g.rush3.x, g.rush3.y, g.rush3.w, g.rush3.h, "silent"),
    btn("rush-4", "", g.rush4.x, g.rush4.y, g.rush4.w, g.rush4.h, "silent"),
    btn("create", "创建房间", g.create.x, g.create.y, g.create.w, g.create.h, "primary"),
    btn("join", "输入房号", g.join.x, g.join.y, g.join.w, g.join.h, "ghost"),
    btn("rank", "", g.rank.x, g.rank.y, g.rank.w, g.rank.h, "silent"),
  ];
}

export function layoutMatching(w: number, h: number, ui: UiState): UiButton[] {
  const bw = Math.min(w - 32, 340);
  const y = Math.min(h * 0.86, h - ui.safeBottom - 64);
  return [btn("cancel-match", "取消寻访", (w - bw) / 2, y, bw, 46, "ghost")];
}

export type PrivateWaitLayout = {
  gridY: number;
  seats: number;
  cells: Rect[];
  copy: Rect;
  ready: Rect | null;
  back: Rect;
};

export function privateWaitLayout(w: number, h: number, ui: UiState): PrivateWaitLayout {
  const capTop = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const capH = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  const metaY = capTop + capH + 14;
  const copy: Rect = { x: 16, y: metaY, w: w - 32, h: 72 };
  const gridY = metaY + copy.h + 44;
  const seats = ui.mode === "RUSH" ? (ui.lobbyMaxSeats === 3 ? 3 : 4) : 2;
  const cols = 2;
  const rows = Math.ceil(seats / cols);
  const gap = 10;
  const areaX = 16;
  const areaW = w - 32;
  const cellW = (areaW - gap) / cols;
  const backY = h - ui.safeBottom - 52;
  const readyH = ui.mode === "RUSH" ? 52 : 0;
  const readyY = backY - (readyH ? readyH + 10 : 0);
  const gridMaxH = readyY - gridY - 28;
  const cellH = Math.min(140, Math.max(100, (gridMaxH - gap * (rows - 1)) / rows));
  const cells: Rect[] = [];
  for (let i = 0; i < seats; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    cells.push({
      x: areaX + col * (cellW + gap),
      y: gridY + row * (cellH + gap),
      w: cellW,
      h: cellH,
    });
  }
  const bw = Math.min(w - 32, 340);
  const cx = (w - bw) / 2;
  return {
    gridY,
    seats,
    cells,
    copy,
    ready: ui.mode === "RUSH" ? { x: cx, y: readyY, w: bw, h: 52 } : null,
    back: { x: cx, y: backY, w: bw, h: 40 },
  };
}

export function layoutPrivate(w: number, h: number, ui: UiState): UiButton[] {
  if (ui.privateMode === "wait") {
    const g = privateWaitLayout(w, h, ui);
    const out: UiButton[] = [btn("back-hall", "返回茶馆", g.back.x, g.back.y, g.back.w, g.back.h, "ghost")];
    out.push(btn("copy-code", "", g.copy.x, g.copy.y, g.copy.w, g.copy.h, "silent"));
    const occupied = ui.mode === "RUSH" ? ui.lobbySeats.length : 1;
    for (let i = occupied; i < g.cells.length; i++) {
      const cell = g.cells[i];
      if (!cell) continue;
      out.push(btn("share-invite", "", cell.x, cell.y, cell.w, cell.h, "silent"));
    }
    if (g.ready) {
      const ready = ui.lobbySeats.some((s) => s.id === ui.userId && s.ready);
      out.push(
        btn(
          "ready",
          ready ? "取消准备" : "准备就绪",
          g.ready.x,
          g.ready.y,
          g.ready.w,
          g.ready.h,
          ready ? "ghost" : "primary",
          ready ? undefined : "房主随时开局",
        ),
      );
    }
    return out;
  }
  const bw = Math.min(280, w - 48);
  const cx = (w - bw) / 2;
  const backY = h - ui.safeBottom - 52;
  const back = btn("back-hall", "返回茶馆", cx, backY, bw, 40, "ghost");
  if (ui.privateMode === "join") {
    const joinY = Math.min(h * 0.82, backY - 58);
    const keys = layoutNumpad(w, h, joinY);
    return [back, ...keys, btn("do-join", "入席对局", cx, joinY, bw, 52, "primary")];
  }
  const start = privateMenuStart(ui, h);
  const half = (bw - 8) / 2;
  return [
    back,
    btn("create-1v1", "开 1 对 1", cx, start, bw, 52, "primary"),
    btn("create-rush-3", "抢答 3 人", cx, start + 64, half, 48, "wood"),
    btn("create-rush-4", "抢答 4 人", cx + half + 8, start + 64, half, 48, "wood"),
    btn("join", "持号入座", cx, start + 124, bw, 48, "ghost"),
  ];
}

function privateMenuStart(ui: UiState, h: number): number {
  const capTop = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const capH = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  return Math.max(h * 0.28, capTop + capH + 72);
}

export function layoutNumpad(w: number, h: number, joinY?: number): UiButton[] {
  const labels = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "清"];
  const ids = ["n1", "n2", "n3", "n4", "n5", "n6", "n7", "n8", "n9", "nbs", "n0", "nclr"];
  const cols = 3;
  const pad = 8;
  const areaW = Math.min(280, w - 48);
  const areaX = (w - areaW) / 2;
  const kw = (areaW - pad * 4) / cols;
  const kh = 50;
  const rows = 4;
  const blockH = rows * kh + (rows - 1) * pad;
  const ceiling = h * 0.36;
  const floor = (joinY ?? h * 0.82) - 12;
  const areaY = Math.max(ceiling, Math.min(h * 0.42, floor - blockH));
  const out: UiButton[] = [];
  for (let i = 0; i < labels.length; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const id = ids[i];
    const label = labels[i];
    if (!id || !label) continue;
    out.push(btn(id, label, areaX + pad + c * (kw + pad), areaY + r * (kh + pad), kw, kh, "ghost"));
  }
  return out;
}

export function layoutResult(w: number, h: number, ui: UiState): UiButton[] {
  const bw = Math.min(280, w - 48);
  const cx = (w - bw) / 2;
  const y = Math.min(h * 0.74, h - ui.safeBottom - 160);
  return [
    btn("share-report", "分享战报", cx, y, bw, 44, "wood"),
    btn("rematch", ui.mode === "RUSH" ? "再开一桌" : "再来一局", cx, y + 52, bw, 52, "primary"),
    btn("back-hall", "回茶馆", cx, y + 108, bw, 40, "ghost"),
  ];
}

export function layoutLeaderboard(w: number, h: number, ui?: UiState): UiButton[] {
  const bw = Math.min(200, w - 80);
  const y = h - (ui?.safeBottom ?? 0) - 72;
  return [btn("close-rank", "收榜", (w - bw) / 2, Math.min(h * 0.86, y), bw, 44, "primary")];
}

export function settingsPanel(w: number, h: number): Rect {
  const pw = Math.min(w - 40, 360);
  const ph = 84 + 4 * 56 + 76;
  const y = Math.max(h * 0.14, (h - ph) * 0.45);
  return { x: (w - pw) / 2, y, w: pw, h: ph };
}

export function layoutSettings(w: number, h: number): UiButton[] {
  const g = settingsPanel(w, h);
  const { x, y, w: pw, h: ph } = g;
  const rows = ["tog-bgm", "tog-sfx", "tog-vib", "privacy-read"];
  const out: UiButton[] = [btn("close-settings", "完成", x + 16, y + ph - 16 - 48, pw - 32, 48, "primary")];
  for (let i = 0; i < rows.length; i++) {
    const id = rows[i];
    if (!id) continue;
    const ry = y + 84 + i * 56;
    out.push(btn(id, "", x + 16, ry, pw - 32, 44, "silent"));
  }
  return out;
}

export type PrivacyLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
  read: Rect;
  reject: Rect;
  agree: Rect;
};

export function privacyLayout(w: number, h: number): PrivacyLayout {
  const x = 24;
  const cardW = w - 48;
  const cardH = Math.min(400, Math.max(332, h * 0.56));
  const y = Math.max(h * 0.14, (h - cardH) * 0.42);
  const pad = 16;
  const btnH = 44;
  const btnY = y + cardH - pad - btnH;
  const gap = 10;
  const inner = cardW - pad * 2;
  const bw = (inner - gap) / 2;
  return {
    x,
    y,
    w: cardW,
    h: cardH,
    read: { x: x + pad, y: btnY - 48, w: inner, h: 36 },
    reject: { x: x + pad, y: btnY, w: bw, h: btnH },
    agree: { x: x + pad + bw + gap, y: btnY, w: bw, h: btnH },
  };
}

export function layoutPrivacy(w: number, h: number): UiButton[] {
  const g = privacyLayout(w, h);
  return [
    btn("privacy-read", "", g.read.x, g.read.y, g.read.w, g.read.h, "silent"),
    btn("privacy-reject", "拒绝", g.reject.x, g.reject.y, g.reject.w, g.reject.h, "ghost"),
    btn("privacy-agree", "同意", g.agree.x, g.agree.y, g.agree.w, g.agree.h, "primary"),
  ];
}

export function layoutBattleHud(
  w: number,
  _bar: { x: number; y: number; w: number; h: number },
  ident: { x: number; y: number; w: number; h: number },
  ui: UiState,
): UiButton[] {
  const hud = opponentHudLayout(w, ui);
  const s = hud.surrender;
  const out: UiButton[] = [btn("surrender", "弃局认输", s.x, s.y, s.w, s.h, "ghost")];
  if (ui.mode !== "RUSH") out.push(...layoutHintPouches(ident));
  return out;
}

export function handleUiTap(ui: UiState, session: Session, id: string, w = 375, h = 667): void {
  switch (id) {
    case "nick-reroll":
      ui.nickname = randomNickname();
      return;
    case "enter":
      session.enterTeahouse();
      return;
    case "privacy-open":
      session.openPrivacyPrompt();
      return;
    case "privacy-agree":
      session.agreePrivacy();
      return;
    case "privacy-reject":
      session.rejectPrivacy();
      return;
    case "privacy-read":
      session.openPrivacyContract();
      return;
    case "match":
      session.match();
      return;
    case "rush":
      ui.hallMenu = "rush-size";
      return;
    case "rush-3":
      session.matchRush(3);
      return;
    case "rush-4":
      session.matchRush(4);
      return;
    case "rush-back":
      ui.hallMenu = "home";
      return;
    case "cancel-match":
      session.cancelMatch();
      return;
    case "create":
      if (ui.screen === "hall") {
        ui.privateMode = "menu";
        ui.screen = "private";
        return;
      }
      session.createRoom();
      return;
    case "create-1v1":
      session.createRoom();
      return;
    case "create-rush-3":
      session.createRushRoom(3);
      return;
    case "create-rush-4":
      session.createRushRoom(4);
      return;
    case "join":
      session.openJoin();
      return;
    case "do-join":
      session.joinRoom();
      return;
    case "copy-code":
      void session.copyRoomCode();
      return;
    case "share-invite":
      session.shareRoom();
      return;
    case "share-report":
      session.shareReport();
      return;
    case "rank":
      session.fetchLeaderboard(w, h);
      return;
    case "close-rank":
      session.closeLeaderboard();
      return;
    case "settings":
      session.openSettings();
      return;
    case "close-settings":
      session.closeSettings();
      return;
    case "tog-bgm":
      session.togglePref("bgmMuted");
      return;
    case "tog-sfx":
      session.togglePref("sfxMuted");
      return;
    case "tog-vib":
      session.togglePref("vibrateMuted");
      return;
    case "back-hall":
      session.backHall();
      return;
    case "rematch":
      session.rematch();
      return;
    case "surrender":
      session.surrender();
      return;
    case "ready":
      session.toggleReady();
      return;
    default:
      break;
  }
  if (id.startsWith("hint-")) {
    session.useHint();
    return;
  }
  if (id.startsWith("scramble-")) {
    session.useScramble();
    return;
  }
  if (id.startsWith("n")) {
    applyNumpad(ui, id);
  }
}

function applyNumpad(ui: UiState, id: string): void {
  if (id === "nbs") {
    ui.joinInput = ui.joinInput.slice(0, -1);
    return;
  }
  if (id === "nclr") {
    ui.joinInput = "";
    return;
  }
  const m = /^n(\d)$/.exec(id);
  const d = m?.[1];
  if (!d) return;
  if (ui.joinInput.length >= 6) return;
  ui.joinInput += d;
}
